import { type FC, useState } from "react";
import PrimaryButton from "../../Common/Buttons/PrimaryButton";
import PasswordInput from "../../Common/Forms/PasswordInput";
import RetractableInput from "../../Common/Forms/RetractableInput";
import PopupManager from "../../Common/Popups/PopupManager";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { setOption } from "../../Utilities/Platform/Options/Options";
import Request from "../../Utilities/Platform/Request";
import LoginUsername from "./Form/LoginUsername";

export const EnterpriseLoginBox: FC = () => {
    const [ username, setUsername ] = useState("");
    const [ password, setPassword ] = useState("");
    const [ enterprise, setEnterprise ] = useState("");
    const [ visible, setVisible ] = useState(false);
    const [ loading, setLoading ] = useState(false);

    const handleSubmit = () => {
        if(!enterprise || !username || !password) {
            PopupManager.warning("Please specify all details before logging in.");

            return;
        }

        setLoading(true);
        Request.get<{
            url: string;
            subdomain: string;
            host: string;
        }>(`/enterprise/login/get-url?enterprise=${enterprise}`)
            .then(response => {
                if(response.status !== 200) {
                    throw new Error("Invalid login");
                }

                // Create and submit a fake form
                const form = document.createElement("form");

                form.method = "POST";
                form.action = response.data.url;
                form.style.visibility = "hidden";

                const usernameEl = document.createElement("input");

                usernameEl.value = username;
                usernameEl.name = "username";
                usernameEl.type = "hidden";

                const passwordEl = document.createElement("input");

                passwordEl.value = password;
                passwordEl.name = "password";
                passwordEl.type = "hidden";

                const subdomainEl = document.createElement("input");

                subdomainEl.value = response.data.subdomain;
                subdomainEl.name = "subdomain";
                subdomainEl.type = "hidden";

                const hostEl = document.createElement("input");

                hostEl.value = response.data.host;
                hostEl.name = "host";
                hostEl.type = "hidden";

                form.appendChild(usernameEl);
                form.appendChild(passwordEl);
                form.appendChild(subdomainEl);
                form.appendChild(hostEl);

                document.body.appendChild(form);

                setOption("loggedout", false);
                form.submit();
            })
            .catch(e => {
                console.error("Unable to login to the enterprise", e);
                PopupManager.error("Unable to log into the enterprise");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <div
            className={
                "fixed bottom-0 right-4 border border-b-0 bg-white shadow-md " +
                "rounded-t-md w-fit max-w-xs transition-all"
            }
        >
            {visible && (
                <div className="mb-4 p-2 md:p-4 pb-0">
                    <p>
                        This allows your controlling enterprise to log in. If you don&apos;t have a controlling
                        enterprise this won&apos;t do anything.
                    </p>
                    <div className="mb-2">
                        <RetractableInput hasValue={!!enterprise} label="Enterprise">
                            {(onFocus, onBlur) => (
                                <LoginUsername
                                    maskUsername={false}
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                    id="shopfront-enterprise-field-e"
                                    name="shopfront-enterprise-field-e"
                                    autoComplete="shopfront-enterprise-field-e"
                                    className="p-4"
                                    value={enterprise}
                                    disabled={loading}
                                    onChange={e => setEnterprise(e.currentTarget.value)}
                                    onClick={e => e.stopPropagation()}
                                />
                            )}
                        </RetractableInput>
                    </div>
                    <div className="mb-2">
                        <RetractableInput hasValue={!!username} label="Username">
                            {(onFocus, onBlur) => (
                                <LoginUsername
                                    maskUsername={false}
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                    id="shopfront-enterprise-field-u"
                                    name="shopfront-enterprise-field-u"
                                    autoComplete="shopfront-enterprise-field-u"
                                    className="p-4"
                                    value={username}
                                    disabled={loading}
                                    onChange={e => setUsername(e.currentTarget.value)}
                                    onClick={e => e.stopPropagation()}
                                />
                            )}
                        </RetractableInput>
                    </div>
                    <div className="mb-2">
                        <RetractableInput hasValue={!!password} label="Password">
                            {(onFocus, onBlur) => (
                                <PasswordInput
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                    id="shopfront-enterprise-field-p"
                                    name="shopfront-enterprise-field-p"
                                    autoComplete="shopfront-enterprise-field-p"
                                    className="p-4"
                                    value={password}
                                    disabled={loading}
                                    onChange={e => setPassword(e.currentTarget.value)}
                                    onClick={e => e.stopPropagation()}
                                    onKeyDown={e => {
                                        if(e.key === "Enter") {
                                            handleSubmit();
                                        }
                                    }}
                                />
                            )}
                        </RetractableInput>
                    </div>
                    <div className="mb-2">
                        <PrimaryButton
                            type="button"
                            classes={[ "w-full p-4!" ]}
                            disabled={loading}
                            onClick={handleSubmit}
                        >
                            {loading ? <FontAwesomeLight icon="spinner-third" spin /> : "Login"}
                        </PrimaryButton>
                    </div>
                </div>
            )}
            <div
                onClick={() => {
                    setVisible(prev => !prev);
                }}
                className="text-right p-2 md:p-4 cursor-pointer"
            >
                Enterprise Login
                <FontAwesomeLight
                    className="ml-2"
                    icon={visible ? "chevron-down" : "chevron-up"}
                />
            </div>
        </div>
    );
};
